import {
  type PaginatedTagListReadable,
  type TagReadable,
  type TagsListData,
  leadsListQueryKey,
  leadsPartialUpdateMutation,
  leadsPipelineListQueryKey,
  leadsRetrieveOptions,
  leadsRetrieveQueryKey,
  leadsSetTagPartialUpdateMutation,
  tagsListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { AddEditTag } from '@crm/features/leads/components'
import { generalTransformResults } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import {
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { type ModalProps, App, Button, Divider, Form, Modal, Tag } from 'antd'
import { Edit2 } from 'iconsax-react'
import { useCallback, useEffect, useState } from 'react'

import type { FieldsProps } from '@crm/types'
import type { AxiosError } from 'axios'

const formName = 'set-tag'

type SetTagProps = {
  leads?: number[]
  dataFields?: FieldsProps
  queryKey?: QueryKey
  onClose: () => void
} & ModalProps

export const SetTag = ({ leads, open, onClose, ...props }: SetTagProps) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const [openModal, setOpenModal] = useState(false)
  const [optionsData, setOptionsData] = useState<
    Array<{ value: number; label: string; payload: TagReadable }>
  >([])
  const [selectedTagsCache, setSelectedTagsCache] = useState<
    Array<{ value: number; label: string; payload: TagReadable }>
  >([])
  const singleItemId = leads?.length === 1 ? leads[0] : null
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const queryClient = useQueryClient()

  const { data: lead, isLoading: isLeadLoading } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(singleItemId) } }),
    enabled: !!singleItemId
  })

  useEffect(() => {
    if (lead?.tags) {
      const tagIds = lead.tags.map(({ id }) => id)

      form.setFieldsValue({ tag: tagIds })
      const cachedTags = generalTransformResults(lead.tags)

      setSelectedTagsCache(cachedTags)
    }
  }, [form, lead])

  const onValuesChange = (changedValues: { tag?: number[] }) => {
    if (changedValues.tag !== undefined) {
      const currentTagIds = changedValues.tag

      setSelectedTagsCache((prev) => {
        const stillSelected = prev.filter((tag) =>
          currentTagIds.includes(tag.value)
        )

        const newSelectedFromOptions = optionsData.filter(
          (opt) =>
            currentTagIds.includes(opt.value) &&
            !stillSelected.find((t) => t.value === opt.value)
        )

        return [...stillSelected, ...newSelectedFromOptions]
      })
    }
  }

  const onSuccessMutate = {
    onError: (e: AxiosError) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsPipelineListQueryKey(), leadsListQueryKey()])
      queryClient.removeQueries({
        queryKey: leadsRetrieveQueryKey({ path: { id: Number(singleItemId) } })
      })
      onClose()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  }

  const { mutate: bulkMutate, isPending: isBulkPending } = useMutation({
    ...leadsSetTagPartialUpdateMutation(),
    ...onSuccessMutate
  })

  const { mutate: singleMutate, isPending: isSinglePending } = useMutation({
    ...leadsPartialUpdateMutation(),
    ...onSuccessMutate
  })

  const onSubmit = () => {
    const values = form.getFieldsValue()

    if (singleItemId) {
      singleMutate({ path: { id: singleItemId }, body: { tags: values.tag } })
    } else {
      bulkMutate({
        body: {
          ...values,
          leads
        }
      })
    }
  }

  const onToggleEditModal = () => setOpenModal((open) => !open)

  const onTagCreated = (tag: TagReadable) => {
    const currentTags = form.getFieldValue('tag') || []

    if (!currentTags.includes(tag.id)) {
      form.setFieldsValue({ tag: [...currentTags, tag.id] })
    }

    const transformedTag = generalTransformResults([tag])[0]

    setSelectedTagsCache((prev) => {
      if (!prev.find((t) => t.value === tag.id)) {
        return [...prev, transformedTag]
      }

      return prev
    })

    onToggleEditModal()
  }

  const getOptions = useCallback(({ options }: { options: TagReadable[] }) => {
    const transformedOptions = generalTransformResults(options)

    setOptionsData(transformedOptions)

    return transformedOptions
  }, [])

  const isPending = isBulkPending || isSinglePending

  return (
    <>
      <Modal
        title={t('contacts.set_tag')}
        cancelText={t('buttons.cancel')}
        onCancel={onClose}
        okText={t('buttons.save')}
        okButtonProps={{ htmlType: 'submit', form: formName }}
        loading={isPending}
        destroyOnHidden
        {...(!openModal && { open })}
        {...props}
      >
        <Form
          form={form}
          name={formName}
          onFinish={onSubmit}
          onValuesChange={onValuesChange}
          layout="vertical"
        >
          <Form.Item name="tag" label={t('tags.tag')}>
            <InfiniteSelect<TagsListData, PaginatedTagListReadable, TagReadable>
              getQueryFunction={tagsListInfiniteOptions}
              {...(singleItemId && {
                mode: 'multiple',
                tagRender(props) {
                  let optionData = optionsData.find(
                    (opt) => opt.value === props.value
                  )

                  if (!optionData) {
                    optionData = selectedTagsCache.find(
                      (opt) => opt.value === props.value
                    )
                  }

                  const color = optionData?.payload?.color
                  const label = optionData?.label || props.label

                  return <Tag color={color}>{label}</Tag>
                }
              })}
              placeholder={t('main.select')}
              loading={isLeadLoading}
              optionRender={({ label, data }) => (
                <Tag color={data?.payload?.color}>{label}</Tag>
              )}
              getOptions={getOptions}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Button
                    type="text"
                    block
                    icon={<Edit2 size={16} />}
                    style={{ justifyContent: 'start' }}
                    onClick={onToggleEditModal}
                  >
                    {t('tags.add')}
                  </Button>
                </>
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      <AddEditTag
        open={openModal}
        onCancel={onToggleEditModal}
        onSuccess={onTagCreated}
      />
    </>
  )
}
